import type { CSSProperties } from 'react';
import React from 'react';
import { TimelineHeadersConsumer } from './HeadersContext';
import { TimelineStateConsumer } from '../timeline/TimelineStateContext';
import { iterateTimes } from '../utility/calendar';
import type { Moment } from 'moment/moment';
import type {
  GetIntervalProps,
  GetIntervalPropsParams,
  IntervalData,
} from './Interval';
import type { TimeSteps, Unit } from '../Timeline';

export type CustomHeaderRendererProps<Data> = {
  timelineContext: {
    canvasTimeEnd: number;
    visibleTimeEnd: number;
    timelineWidth: number;
    visibleTimeStart: number;
    canvasTimeStart: number;
  };
  headerContext: {
    unit: Unit;
    intervals: IntervalData[];
  };
  getIntervalProps: (props?: GetIntervalPropsParams) => GetIntervalProps;
  getRootProps: (propsToOverride?: { style?: React.CSSProperties }) => {
    style: React.CSSProperties;
  };
  showPeriod: (startDate: Moment | number, endDate: Moment | number) => void;
  data: Data;
};

export type CustomHeaderProps<Data> = {
  //component props
  children: React.ComponentType<CustomHeaderRendererProps<Data>>;
  unit: Unit;
  //Timeline context
  timeSteps: TimeSteps;
  timelineWidth: number;
  visibleTimeStart: number;
  visibleTimeEnd: number;
  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  showPeriod: (startDate: Moment | number, endDate: Moment | number) => void;
  headerData: Data;
  getLeftOffsetFromDate: (date: number) => number;
  height: number;
};

const getHeaderIntervals = ({
  canvasTimeStart,
  canvasTimeEnd,
  unit,
  timeSteps,
  getLeftOffsetFromDate,
}: {
  canvasTimeStart: number;
  canvasTimeEnd: number;
  unit: Unit;
  timeSteps: TimeSteps;
  getLeftOffsetFromDate: (date: number) => number;
}) => {
  const intervals: IntervalData[] = [];
  iterateTimes(
    canvasTimeStart,
    canvasTimeEnd,
    unit,
    timeSteps,
    (startTime, endTime) => {
      const left = getLeftOffsetFromDate(startTime.valueOf());
      const right = getLeftOffsetFromDate(endTime.valueOf());
      const labelWidth = right - left;
      intervals.push({
        startTime,
        endTime,
        labelWidth,
        left,
      });
    },
  );
  return intervals;
};

type CustomHeaderState = {
  intervals: IntervalData[];
  unit: Unit;
  timeSteps: TimeSteps;
  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  showPeriod: (startDate: Moment | number, endDate: Moment | number) => void;
  getLeftOffsetFromDate: (date: number) => number;
};

export class CustomHeader<Data> extends React.Component<
  CustomHeaderProps<Data>,
  CustomHeaderState
> {
  constructor(props: CustomHeaderProps<Data>) {
    super(props);
    const {
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
      canvasWidth,
      showPeriod,
    } = props;

    const intervals = getHeaderIntervals({
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
    });

    this.state = {
      intervals,
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
      canvasWidth,
      showPeriod,
    };
  }

  static getDerivedStateFromProps<Data>(
    props: CustomHeaderProps<Data>,
    state: CustomHeaderState,
  ) {
    const {
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
      canvasWidth,
      showPeriod,
    } = props;

    if (
      canvasTimeStart === state.canvasTimeStart &&
      canvasTimeEnd === state.canvasTimeEnd &&
      canvasWidth === state.canvasWidth &&
      unit === state.unit &&
      timeSteps === state.timeSteps &&
      getLeftOffsetFromDate === state.getLeftOffsetFromDate &&
      showPeriod === state.showPeriod
    ) {
      return null;
    }

    const intervals = getHeaderIntervals({
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
    });

    return {
      intervals,
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      timeSteps,
      getLeftOffsetFromDate,
      canvasWidth,
      showPeriod,
    };
  }

  shouldComponentUpdate(nextProps: CustomHeaderProps<Data>) {
    return (
      nextProps.canvasTimeStart !== this.props.canvasTimeStart ||
      nextProps.canvasTimeEnd !== this.props.canvasTimeEnd ||
      nextProps.canvasWidth !== this.props.canvasWidth ||
      nextProps.unit !== this.props.unit ||
      nextProps.timeSteps !== this.props.timeSteps ||
      nextProps.showPeriod !== this.props.showPeriod ||
      nextProps.children !== this.props.children ||
      nextProps.headerData !== this.props.headerData
    );
  }

  getRootProps = (
    props: {
      style?: CSSProperties;
    } = {},
  ): {
    style: CSSProperties;
  } => {
    const { style } = props;
    return {
      style: Object.assign({}, style ? style : {}, {
        position: 'relative',
        width: this.props.canvasWidth,
        height: this.props.height,
      }),
    };
  };

  getIntervalProps = (
    props: {
      style?: CSSProperties;
      interval?: IntervalData;
    } = {},
  ) => {
    const { interval, style } = props;
    if (!interval)
      throw new Error('you should provide interval to the prop getter');
    const { startTime, labelWidth, left } = interval;
    return {
      style: this.getIntervalStyle({
        style,
        labelWidth,
        left,
      }),
      key: `label-${startTime.valueOf()}`,
    };
  };

  getIntervalStyle = ({
    left,
    labelWidth,
    style,
  }: {
    left?: number;
    labelWidth: number;
    style?: CSSProperties;
  }): CSSProperties => {
    return {
      ...style,
      left,
      width: labelWidth,
      position: 'absolute',
    };
  };

  getStateAndHelpers: () => CustomHeaderRendererProps<Data> = () => {
    const {
      canvasTimeStart,
      canvasTimeEnd,
      unit,
      showPeriod,
      timelineWidth,
      visibleTimeStart,
      visibleTimeEnd,
      headerData,
    } = this.props;
    //TODO: only evaluate on changing params
    return {
      timelineContext: {
        timelineWidth,
        visibleTimeStart,
        visibleTimeEnd,
        canvasTimeStart,
        canvasTimeEnd,
      },
      headerContext: {
        unit,
        intervals: this.state.intervals,
      },
      getRootProps: this.getRootProps,
      getIntervalProps: this.getIntervalProps,
      showPeriod,
      data: headerData,
    };
  };

  render() {
    const props = this.getStateAndHelpers();
    const Renderer = this.props.children;
    return <Renderer {...props} />;
  }
}

const CustomHeaderWrapper = function <Data>({
  children,
  unit,
  headerData,
  height,
}: Pick<
  CustomHeaderProps<Data>,
  'children' | 'unit' | 'headerData' | 'height'
>) {
  return (
    <TimelineStateConsumer>
      {({ getTimelineState, showPeriod, getLeftOffsetFromDate }) => {
        return (
          <TimelineHeadersConsumer>
            {({ timeSteps }) => {
              const timelineState = getTimelineState();
              return (
                <CustomHeader
                  timeSteps={timeSteps}
                  showPeriod={showPeriod}
                  unit={unit ? unit : timelineState.timelineUnit}
                  {...timelineState}
                  headerData={headerData}
                  getLeftOffsetFromDate={getLeftOffsetFromDate}
                  height={height}
                >
                  {children}
                </CustomHeader>
              );
            }}
          </TimelineHeadersConsumer>
        );
      }}
    </TimelineStateConsumer>
  );
};

CustomHeaderWrapper.defaultProps = {
  height: 30,
};

export default CustomHeaderWrapper;
